import React from 'react';
import PropTypes from 'prop-types';

import {CardActionArea, Chip, Stack, Typography} from 'views/components';
import {translate} from 'views/module';

import featureFlags from 'utils/feature_flags/feature_flags';

// Same chip idiom as the case-list mark chip: absence is the "off" state, so
// there is no counterpart badge for a guide the teacher does not mark.
const MARKING_BADGE_SX = {
  'height': '20px',
  'flexShrink': 0,
  'fontSize': '11px',
  'fontWeight': 600,
  'color': '#008A00',
  'backgroundColor': '#E3FAE5',
  'border': '1px solid #BCE9C0',
  'borderRadius': '5px',
  '& .MuiChip-label': {
    px: '7px',
  },
};

const DomainGuideCard = (props) => {
  const {
    createdByLabel,
    domainGuideData,
  } = props;

  const emptyValue = translate('domainGuide.Detail.EmptyValue');
  // Gated with the marking controls that write the field: when those are
  // hidden, marking is not a concept this admin can see or change anywhere.
  const isMarkingEnabled = featureFlags.oversightMarkingControlsEnabled &&
    Boolean(domainGuideData.marking?.enabled);
  const createdAtDate = new Date(domainGuideData.createdAt);
  const createdAtLabel = Number.isNaN(createdAtDate.getTime()) ?
    emptyValue :
    createdAtDate.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const updatedAtDate = new Date(domainGuideData.updatedAt);
  const updatedAtLabel = Number.isNaN(updatedAtDate.getTime()) ?
    emptyValue :
    updatedAtDate.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const detailSections = [
    {
      key: 'domainGuideRn',
      label: translate('domainGuide.Detail.Fields.DomainGuideId'),
      value: domainGuideData.domainGuideRn || emptyValue,
    },
    {
      key: 'description',
      label: translate('domainGuide.Detail.Fields.Description'),
      value: domainGuideData.description || emptyValue,
    },
    {
      key: 'status',
      label: translate('domainGuide.Detail.Fields.Status'),
      value: domainGuideData.status ? translate(`domainGuide.Status.${domainGuideData.status}.Title`) : emptyValue,
    },
    {
      key: 'createdBy',
      label: translate('domainGuide.Detail.Fields.CreatedBy'),
      value: createdByLabel || domainGuideData.createdBy || emptyValue,
    },
    {
      key: 'createdAt',
      label: translate('domainGuide.Detail.Fields.CreatedAt'),
      value: createdAtLabel,
    },
    {
      key: 'updatedAt',
      label: translate('domainGuide.Detail.Fields.UpdatedAt'),
      value: updatedAtLabel,
    },
  ];

  return (
    <CardActionArea
      to={`/domain-guide/${domainGuideData.domainGuideRn}`}
      sx={{
        'display': 'block',
        'textAlign': 'left',
        'backgroundColor': '#FFFFFF',
        'border': '1px solid #E8E4DE',
        'borderRadius': '8px',
        'p': 1.5,
        '&:hover': {
          backgroundColor: '#F8F6F3',
        },
      }}
    >
      <Stack sx={{gap: 1}}>
        <Stack direction='row' sx={{gap: 1, alignItems: 'center'}}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#1A1916',
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            {domainGuideData.name || emptyValue}
          </Typography>
          {
            isMarkingEnabled &&
            <Chip
              label={translate('domainGuide.Detail.Marking.EnabledBadge')}
              size='small'
              sx={MARKING_BADGE_SX}
            />
          }
        </Stack>
        <Stack sx={{gap: 0.75}}>
          {
            detailSections.map((section) => (
              <Stack key={section.key} sx={{gap: '2px'}}>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#A8A49E',
                    textTransform: 'uppercase',
                  }}
                >
                  {section.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#6B6760',
                    lineHeight: '16px',
                    wordBreak: 'break-word',
                  }}
                >
                  {section.value}
                </Typography>
              </Stack>
            ))
          }
        </Stack>
      </Stack>
    </CardActionArea>
  );
};

DomainGuideCard.propTypes = {
  createdByLabel: PropTypes.string,
  domainGuideData: PropTypes.object.isRequired,
};

export default React.memo(DomainGuideCard);
